import _ from "lodash";
import { useState } from "react";
import { SketchPicker } from "react-color";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../components/Buttons";
import {
  RadioInput,
  RadioInputGroup,
  SelectInput,
  TextInput,
  CheckboxInput,
} from "../components/Inputs";
import { EnumEntityModal, EnumEntityModal2, StdEntityModal } from "../components/Modals";
import { useLoaderStore } from "../context";
import { useAlert, useApi } from "../hooks";
import {
  BACKEND_FRAMEWORKS,
  DATABASES,
  FRONTEND_FRAMEWORKS,
} from "../utils/frameworks";

export default function BuildApp() {
  const { post } = useApi();
  const alert = useAlert();

  const [projectName, setProjectName] = useState("");
  const [filePath, setFilePath] = useState("");
  const navigate = useNavigate();
  const [backendChoice, setBackendChoice] = useState(
    BACKEND_FRAMEWORKS[0].label
  );
  const [frontend, setFrontend] = useState("React");
  const [database, setDatabase] = useState("SqlServer");
  const [clientName, setClientName] = useState("");
  const [entities, setEntities] = useState([]);
  const { setShowLoader } = useLoaderStore();
  const [projectColor, setProjectColor] = useState("#37d67a");
  const [radioValue, setRadioValue] = useState();
  const [enumModalVisible, setEnumModalVisible] = useState(false);
  const [standardModalVisible, setStandardModalVisible] = useState(false);
  const [tableNames, setTableNames] = useState([]);
  const [downloadAsZipFile, setDownloadAsZipFile] = useState(false);

  const handleSubmit = async () => {
    if (!isValid()) return;

    setShowLoader(true);

    await post("generator/GenerateApp", {
      ProjectName: projectName,
      RepositoryName: filePath,
      ClientName: clientName,
      BackendFramework: backendChoice.value,
      FrontendFramework: frontend.value,
      Database: database.value,
      PrimaryColor: projectColor,
      Entities: entities,
      DownloadAsZipFile: downloadAsZipFile,
    })
      .then(() => {
        alert("Success!", "Successfully created app", "success");
        navigate("/");
      })
      .catch((err) => {
        alert(
          "Unsuccessful",
          err.data.message || "Unable to generate project",
          "error"
        );
        console.error(err);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const isValid = () => {
    if (!projectName || !projectName.trim()) {
      alert("Invalid input", "Please enter a valid project name", "warning");
      return false;
    }
    if (!downloadAsZipFile && (!filePath || !filePath.trim())) {
      alert(
        "Invalid input",
        "Please enter a valid  repository name",
        "warning"
      );
      return false;
    }
    if (!clientName || !clientName.trim()) {
      alert("Invalid input", "Please enter a valid client name", "warning");
      return false;
    }
    if (!frontend) {
      alert("Invalid input", "Please enter a frontend framework", "warning");
      return false;
    }

    if (!backendChoice) {
      alert("Invalid input", "Please enter a backend framework", "warning");
      return false;
    }
    if (!database) {
      alert("Invalid input", "Please enter a database type", "warning");
      return false;
    }
    if (!projectColor) {
      alert("Invalid input", "Please enter a valid color", "warning");
      return false;
    }
    return true;
  };

  const handleEntityAdd = () => {
    if (radioValue === "enum") {
      setEnumModalVisible(true);
    }
    if (radioValue === "standard") {
      setStandardModalVisible(true);
    }
  };

  const addEnumToEntitiesList = (enumTableName, formattedEnum) => {
    console.log(enumTableName, formattedEnum);
    setEntities([
      ...entities,
      {
        name: enumTableName,
        enum: {
          values: formattedEnum,
        },
        properties: [
          {
            name: "Name",
            type: 1,
            isNullable: false,
            maxLength: 30,
          },
        ],
      },
    ]);
    setTableNames([...tableNames, enumTableName]);
  };

  const addEntityToEntitiesList = (
    tableName,
    apiSpike,
    domainSelect,
    softUser,
    tableProperties
  ) => {
    setEntities([
      ...entities,
      {
        name: tableName,
        includeInApiSpike: apiSpike,
        domain: domainSelect ? domainSelect : null,
        isSoftDeletable: softUser,
        isUserManaged: softUser,
        properties: tableProperties,
      },
    ]);
    setTableNames([...tableNames, tableName]);
  };

  return (
    <>
      <h4 className="text-xl mt-8 px-4 w-full border-b">Project information</h4>
      {/* <div className="px-4 my-8">
        <CheckboxInput
          label="Download project as a zip (instead of creating a repository in the Sightsource github organization)"
          checked={downloadAsZipFile}
          setChecked={setDownloadAsZipFile}
        />
      </div> */}

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 lg:gap-x-8 gap-y-4">
        <div>
          <TextInput
            label="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </div>
        {downloadAsZipFile ? null : (
          <div>
            <TextInput
              label="Repository Name"
              value={filePath}
              onChange={(e) => setFilePath(e.target.value)}
            />
          </div>
        )}
        <div>
          <TextInput
            label="Client Name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>
        <div>
          <SelectInput
            options={BACKEND_FRAMEWORKS}
            label="Backend Framework"
            value={backendChoice}
            onChange={setBackendChoice}
            placeholder={backendChoice}
          />
        </div>

        <div>
          <SelectInput
            options={FRONTEND_FRAMEWORKS}
            label="Frontend Framework"
            onClear={() => setFrontend({})}
            value={frontend}
            onChange={setFrontend}
            placeholder={frontend}
          />
        </div>

        <div>
          <SelectInput
            options={DATABASES}
            label="Database"
            onClear={() => setDatabase({})}
            value={database}
            onChange={setDatabase}
            placeholder={database}
          />
        </div>
      </div>
      {/* Project color */}
      <div className="px-4">
        <div className="text-sm text-gray-600" label="Choose a Project Color">
          Choose a Project Color
        </div>
        <SketchPicker
          color={projectColor}
          onChangeComplete={(color) => setProjectColor(color.hex)}
        />
        <div
          className="mt-2 text-center rounded-md w-24 py-3 px-1 text-white border"
          style={{
            backgroundColor: `${projectColor}`,
          }}
        >
          {projectColor}
        </div>
      </div>

      <h4 className="text-xl mt-8 px-4 w-full border-b">
        Create entities &#40;tables&#41;
      </h4>
      <div className="px-4 italic">
        Note: when naming entities, use singular names -- not plural. Also just
        go with pascal naming convention. If you choose postgres, sgb will auto
        convert to snake case. Eg do FavoriteColor for an entity name
      </div>

      {tableNames.length > 0 ? (
        <div className="px-4">
          <h3 className="mt-2">Current entities</h3>
          <div className="mt-2">
            <ol>
              {_.map(tableNames, (x, index) => (
                <li key={index} className="text-gray-400">
                  {x}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}

      <div className="mx-4 mt-4">
        <h6 className="text-gray-600 mb-2">Select entity type</h6>
        <RadioInputGroup onChange={setRadioValue}>
          <RadioInput
            label="Enum"
            value="enum"
            groupName="tableType"
            checked={radioValue === "enum"}
          />
          <RadioInput
            label="Standard"
            value="standard"
            groupName="tabltype"
            checked={radioValue === "standard"}
          />
        </RadioInputGroup>

        <div className="w-48 my-4">
          <PrimaryButton
            text={`Create ${radioValue || ""} entity`}
            disabled={!radioValue}
            onClick={handleEntityAdd}
          />
        </div>
      </div>
      {enumModalVisible && (
        <EnumEntityModal2
          isOpen={enumModalVisible}
          onClose={() => setEnumModalVisible(false)}
          onAddEnum={addEnumToEntitiesList}
        />
      )}

      {standardModalVisible && (
        <StdEntityModal
          isOpen={standardModalVisible}
          onClose={() => setStandardModalVisible(false)}
          onAddStandardEntity={addEntityToEntitiesList}
        />
      )}

      <div className="px-4 mt-12 mb-4">
        <PrimaryButton text="Generate" onClick={handleSubmit}></PrimaryButton>
      </div>
    </>
  );
}
